/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

/* eslint-disable react/jsx-sort-props */

import { AnyValidator, IntegerValidator, NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  ButtonList,
  ICON_LOCK,
  ICON_LOCK_OPEN,
  SPINNER_TYPE_REGULAR,
  SelectField,
  SelectFieldOption,
  SelectFieldProps,
  Spinner,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  TextField,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { useScvSchema } from 'apps/customer-data-platform/src/Services/single-customer-view';
import { UniqueValueValidator } from 'apps/customer-data-platform/src/Utilities/UniqueValueValidator/UniqueValueValidator';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import every from 'lodash/every';
import has from 'lodash/has';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import property from 'lodash/property';
import set from 'lodash/set';
import some from 'lodash/some';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EVENT_RULE_LOGIC_OPERATOR_AND,
  EVENT_RULE_TYPE_CUSTOM,
  EVENT_RULE_TYPE_LOGIN,
  EVENT_RULE_TYPE_REQUEST,
  EVENT_RULE_TYPE_VISIT,
  EVENT_SUBSCRIPTION_ORIGIN_CLIENT_LAYER,
  EVENT_SUBSCRIPTION_ORIGIN_CLOUDFLARE,
  EntitySegmentEventSubscription,
  getSegmentationService,
  useSegmentEvents,
} from '../../../../Services/segmentation';
import { RegExpValidator } from '../../../../Utilities/RegExpValidator/RegExpValidator';
import { SCV_SYSTEM_ATTRIBUTES } from '../../../CustomerProfile/attributes/ScvSystemAttributes.constants';
import {
  EVENT_BUILDER_ATTRIBUTES_ID,
  EVENT_BUILDER_DATA_IDS,
  EVENT_BUILDER_FORM_ID_PROPERTY_MAP,
  EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS,
} from '../../EventBuilder.constants';
import { attributeNameSelectOptionMap, eventOptions } from './EventBuilderWebBehaviorForm.constants';
import { EventBuilderWebBehaviorFormProps } from './EventBuilderWebBehaviorForm.props';
import {
  EventBuilderError,
  EventBuilderFieldValidationResponse,
  EventBuilderPropertyPair,
} from './EventBuilderWebBehaviorForm.refs';
import { EventBuilderWebBehaviorAttributes } from './elements/EventBuilderWebBehaviorAttributes/EventBuilderWebBehaviorAttributes';
import { EventBuilderWebBehaviorAttributesProps } from './elements/EventBuilderWebBehaviorAttributes/EventBuilderWebBehaviorAttributes.props';

/**
 * Generate an attribute id
 * @param {string} name The name.
 * @returns {string} Kebab case string.
 */
export function generateAttributeId(name: string): string {
  const words = name.split(/[^a-z0-9]/i);
  return words.map((word) => word.toLowerCase()).join(`-`);
}

/**
 * The create and update a web behavior event form.
 */
export const EventBuilderWebBehaviorForm = (props: EventBuilderWebBehaviorFormProps): JSX.Element => {
  const { event, resetChanges, setEvent } = props;
  const { t } = useTranslation();
  const [errors, setErrors] = useState<EventBuilderError>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { eventId } = useParams();
  const { updateActiveToast } = useToastStore();
  const schema = useScvSchema();
  const navigate = useNavigate();
  const [initialEventState] = useState(event);
  const isCreate: boolean = useMemo((): boolean => {
    return isUndefined(eventId);
  }, [eventId]);
  const isAttributeIdEditable: boolean = useMemo((): boolean => {
    return some([
      isUndefined(eventId),
      // @ts-ignore.
      initialEventState?.attributeid === ``,
      // @ts-ignore.
      isUndefined(initialEventState?.attributeid),
    ]);
  }, [eventId, initialEventState]);

  // Insights service
  const segmentationService = getSegmentationService();

  // Existing events.
  const eventsList = useSegmentEvents();

  const labels = useMemo(() => {
    return {
      addAttribute: t(CDP_STRINGS.addAttribute),
      addEvent: t(CDP_STRINGS.eventCreateFeed),
      attributePlaceholder: t(CDP_STRINGS.attributeEnter),
      attributeTextfieldFrom: t(CDP_STRINGS.attributeTextfieldFrom),
      attributeTextfieldTo: t(CDP_STRINGS.attributeTextfieldTo),
      cancel: t(CDP_STRINGS.cancel),
      deleteMapping: t(CDP_STRINGS.attributeDeleteMapping),
      eventBuilderAttributeIdLabel: t(CDP_STRINGS.eventBuilderAttributeIdLabel),
      eventBuilderAttributeIdNote: t(CDP_STRINGS.eventBuilderAttributeIdNote),
      eventBuilderAttributeIdPlaceholder: t(CDP_STRINGS.eventBuilderAttributeIdPlaceholder),
      eventCustomId: t(CDP_STRINGS.eventCustomId),
      eventCustomIdNote: t(CDP_STRINGS.eventCustomIdNote),
      eventCustomIdPlaceholder: t(CDP_STRINGS.eventCustomIdPlaceholder),
      eventDetails: t(CDP_STRINGS.eventDetails),
      eventNameLabel: t(CDP_STRINGS.eventBuilderNameLabel),
      eventNameNote: t(CDP_STRINGS.eventBuilderNameNote),
      eventNamePlaceholder: t(CDP_STRINGS.eventBuilderNamePlaceholder),
      eventSourceNote: t(CDP_STRINGS.eventSourceNote),
      eventTimePeriodLabel: t(CDP_STRINGS.eventTimePeriodLabel),
      eventTimePeriodNote: t(CDP_STRINGS.eventTimePeriodNote),
      eventTimePeriodPlaceholder: t(CDP_STRINGS.eventTimePeriodPlaceholder),
      eventType: t(CDP_STRINGS.eventType),
      requiredSection: t(CDP_STRINGS.requiredSection),
      saveChanges: t(CDP_STRINGS.saveChanges),
      selectEventTypePlaceholder: t(CDP_STRINGS.selectEventTypePlaceholder),
      sourceLabel: t(CDP_STRINGS.sourceLabel),
      tableHeaderFrom: t(CDP_STRINGS.attributeTableHeaderFrom),
      tableHeaderTo: t(CDP_STRINGS.attributeTableHeaderTo),
      toastEventSuccessAddedMessage: t(CDP_STRINGS.toastEventSuccessAddedMessage),
      toastEventSuccessAddedMessageEnd: t(CDP_STRINGS.toastEventSuccessAddedMessageEnd),
      toastEventSuccessAddedTitle: t(CDP_STRINGS.toastEventSuccessAddedTitle),
      toastEventSuccessUpdatedTitle: t(CDP_STRINGS.toastEventSuccessUpdatedTitle),
      websiteBehavior: t(CDP_STRINGS.websiteBehavior),
    };
  }, [t]);

  const errorLabels = useMemo(() => {
    return {
      attributeFormat: t(CDP_STRINGS.errorAttributeFormat),
      errorIdInUse: t(CDP_STRINGS.errorIdInUse),
      errorKeyValue: t(CDP_STRINGS.errorKeyValue),
      errorNameInUse: t(CDP_STRINGS.errorNameInUse),
      errorRequiredAttribute: t(CDP_STRINGS.errorRequiredAttribute),
      errorRequiredField: t(CDP_STRINGS.errorRequiredField),
      errorRequiredKey: t(CDP_STRINGS.errorRequiredKey),
      errorRequiredRule: t(CDP_STRINGS.errorRequiredRule),
      errorRequiredValue: t(CDP_STRINGS.errorRequiredValue),
      numbersOnly: t(CDP_STRINGS.errorCountNumber),
      requiredAttribute: t(CDP_STRINGS.errorRequiredAttribute),
      requiredCustomId: t(CDP_STRINGS.errorRequiredCustomName),
      requiredEventName: t(CDP_STRINGS.errorRequiredEventName),
      toastErrorLabel: t(CDP_STRINGS.toastErrorLabel),
      toastErrorTitle: t(CDP_STRINGS.toastErrorTitle),
      toastEventFailAddedMessage: t(CDP_STRINGS.toastEventFailAddedMessage),
      toastEventFailAddedMessageEnd: t(CDP_STRINGS.toastEventFailAddedMessageEnd),
      toastEventFailDeletedMessage: t(CDP_STRINGS.toastEventFailDeletedMessage),
      toastEventFailUpdatedMessageEnd: t(CDP_STRINGS.toastEventFailUpdatedMessageEnd),
      uniqueAttribute: t(CDP_STRINGS.errorAttributeUnique),
    };
  }, [t]);

  /**
   * Update errors removing any errors that no longer have any elements associated with them.
   * @param {EventBuilderError} errors Errors to update.
   * @returns {void}
   */
  const updateErrors = useCallback((errors: EventBuilderError): void => {
    const nextErrors: EventBuilderError = {};
    Object.entries(errors)
      .map(([key, value]) => {
        const lookupKey = property(key)(EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS) || key;
        return [key, value, document.querySelector(`[id="${lookupKey}"]`)];
      })
      .filter(([, , elem]) => {
        return elem !== null;
      })
      .forEach(([key, value]) => {
        nextErrors[key as string] = value as string | null;
      });
    setErrors(nextErrors);
  }, []);

  /**
   * Get a validation response as an object.
   * @param {string} fieldID The field id (or error object key).
   * @param {string | null} errorMessage String error message for failures, or null for no error state.
   * @returns {EventBuilderError} An event builder error that can be used to update internal error state.
   */
  const getValidationResponse = useCallback((fieldId: string, errorMessage: string | null): EventBuilderError => {
    return { [fieldId]: errorMessage };
  }, []);

  /**
   * Does field validation on a given value and set of validators.
   * @param {string} value The value to check.
   * @param {Array<AnyValidator>} validationMap An array of validators to use.
   * @returns {EventBuilderFieldValidationResponse} Validation responses including error messages and validation state.
   */
  const doFieldValidation = useCallback(
    (value: string, validationMap: Array<AnyValidator>): EventBuilderFieldValidationResponse => {
      const messages = validationMap
        .map((v) => v.validate(value) as EventBuilderError)
        .reduce((prev, curr) => {
          const entries = Object.entries(curr);
          const [key, message] = entries[0];
          if (!has(prev, key) || (isString(message) && !isString(prev[key]))) {
            prev[key] = message;
          }
          return prev;
        }, {});
      return {
        isValid: !some(Object.values(messages)),
        messages,
      };
    },
    [],
  );

  // Validation instances.
  const fieldValidations = useMemo((): Record<
    string,
    Array<AnyValidator> | ((a: string, b: string) => Array<AnyValidator>)
  > => {
    const uniqueNameInSchemaValidator = new UniqueValueValidator<object | null>(
      () => getValidationResponse(`name`, errorLabels.errorNameInUse),
      () => getValidationResponse(`name`, null),
    );
    const uniqueAttributeIdInSchemaValidator = new UniqueValueValidator<object | null>(
      () => getValidationResponse(`id`, errorLabels.errorIdInUse),
      () => getValidationResponse(`id`, null),
    );
    if (schema !== null) {
      const properties = Object.keys(property(`properties`)(schema) as object);
      const eventProperties = Object.keys(property(`x-scv-event-properties`)(schema) || {});
      uniqueAttributeIdInSchemaValidator.setAllowedCount(isAttributeIdEditable ? 0 : 1);
      uniqueAttributeIdInSchemaValidator.setValues([...eventProperties, ...properties], [``]);

      const titles = Object.values(property(`properties`)(schema) as object)
        .map((prop) => {
          return property(`title`)(prop) || false;
        })
        .filter((prop) => prop !== false);
      const eventTitles = Object.values(property(`x-scv-event-properties`)(schema) || {})
        .map((prop) => {
          return property(`title`)(prop) || false;
        })
        .filter((prop) => prop !== false);
      const systemTitles = Object.values(SCV_SYSTEM_ATTRIBUTES)
        .map((prop) => {
          return property(`title`)(prop) || false;
        })
        .filter((prop) => prop !== false);
      uniqueNameInSchemaValidator.setAllowedCount(isCreate ? 0 : 1);
      uniqueNameInSchemaValidator.setValues([...titles, ...eventTitles, ...systemTitles], [``]);
    }

    const attributeIdPatternValidator = new RegExpValidator<object | null>(
      () => getValidationResponse(`id`, `Enter lowercase letters, numbers, and symbol (-) only`),
      () => getValidationResponse(`id`, null),
    );

    attributeIdPatternValidator.setExpr(/^[a-z0-9-]+$/);

    return {
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.name]]: [
        new NotEmptyValidator<object | null>(
          () => getValidationResponse(`name`, errorLabels.errorRequiredField),
          () => getValidationResponse(`name`, null),
        ),
        uniqueNameInSchemaValidator,
      ],
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.id]]: [
        new NotEmptyValidator<object | null>(
          () => getValidationResponse(`id`, errorLabels.errorRequiredField),
          () => getValidationResponse(`id`, null),
        ),
        attributeIdPatternValidator,
        uniqueAttributeIdInSchemaValidator,
      ],
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.uniqueCustomId]]: [
        new NotEmptyValidator<object | null>(
          () => getValidationResponse(`custom`, errorLabels.errorRequiredField),
          () => getValidationResponse(`custom`, null),
        ),
      ],
      [EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.time]]: [
        new IntegerValidator<object | null>(
          () => getValidationResponse(`time`, errorLabels.numbersOnly),
          () => getValidationResponse(`time`, null),
          true,
        ),
      ],
      // 'Dynamic' attribute from field validator.
      attribute: (prefix: string, errorKey: string): Array<AnyValidator> => {
        const keyValidator = new RegExpValidator<object | null>(
          () => getValidationResponse(errorKey, errorLabels.errorKeyValue),
          () => getValidationResponse(errorKey, null),
        );
        keyValidator.setExpr(/^[a-zA-Z0-9\-_$]+$/);
        switch (prefix) {
          case 'name':
            return [
              new NotEmptyValidator<object | null>(
                () => getValidationResponse(errorKey, errorLabels.errorRequiredAttribute),
                () => getValidationResponse(errorKey, null),
              ),
            ];
          case 'comparison':
            return [
              new NotEmptyValidator<object | null>(
                () => getValidationResponse(errorKey, errorLabels.errorRequiredRule),
                () => getValidationResponse(errorKey, null),
              ),
            ];
          case 'key':
            return [
              new NotEmptyValidator<object | null>(
                () => getValidationResponse(errorKey, errorLabels.errorRequiredKey),
                () => getValidationResponse(errorKey, null),
              ),
              keyValidator,
            ];
          case 'value':
            return [
              new NotEmptyValidator<object | null>(
                () => getValidationResponse(errorKey, errorLabels.errorRequiredValue),
                () => getValidationResponse(errorKey, null),
              ),
            ];
          default:
            // No validation.
            return [];
        }
      },
    };
  }, [
    errorLabels.errorIdInUse,
    errorLabels.errorKeyValue,
    errorLabels.errorNameInUse,
    errorLabels.errorRequiredAttribute,
    errorLabels.errorRequiredField,
    errorLabels.errorRequiredKey,
    errorLabels.errorRequiredRule,
    errorLabels.errorRequiredValue,
    errorLabels.numbersOnly,
    getValidationResponse,
    isAttributeIdEditable,
    isCreate,
    schema,
  ]);

  /**
   * Validate and update a non-dynamic event value if valid.
   * @param {string} propertyKey The known property key.
   * @param {string} propertyValue The known property value.
   * @returns {void}
   */
  const updateEventValue = useCallback(
    (values: Array<EventBuilderPropertyPair>): void => {
      let nextEvent = { ...event };
      let nextErrors = { ...errors };
      const validations: Array<boolean> = [];
      values.forEach((value) => {
        const { key: propertyKey, value: propertyValue } = value;
        const validationMap: Array<AnyValidator> = fieldValidations[propertyKey] as Array<AnyValidator>;
        const { messages, isValid } = doFieldValidation(propertyValue, validationMap);
        validations.push(isValid);
        nextErrors = { ...nextErrors, ...messages };
        if (isValid) {
          nextEvent = set(nextEvent, propertyKey, propertyValue);
        }
      });
      updateErrors(nextErrors);
      if (every(validations)) {
        setEvent(nextEvent);
      }
    },
    [doFieldValidation, errors, event, fieldValidations, setEvent, updateErrors],
  );

  /**
   * Triggers validation of all fields in the component.
   * Note: uses actual input values rather than event state.
   */
  const validateAllFields = useCallback((): boolean => {
    let nextErrors: EventBuilderError = {};
    const valid: Array<boolean> = [];

    // Find inputs for raw values.
    Object.entries(EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS)
      .map(([, id]): Array<string | Element> => {
        const elem = document.getElementById(id) as HTMLInputElement;
        return [id, elem];
      })
      .filter(([id, elem]): boolean => {
        // Unique custom id only appears in certain circumstances so don't try to validate iif it's not there.
        if (id === `uniqueCustomId` && !elem) {
          return false;
        }
        return elem !== null;
      })
      .forEach(([id, elem]): void => {
        const validationMap: Array<AnyValidator> | undefined = property(
          EVENT_BUILDER_FORM_ID_PROPERTY_MAP[id as string],
        )(fieldValidations) as Array<AnyValidator> | undefined;
        if (!isUndefined(validationMap) && elem) {
          const { messages, isValid } = doFieldValidation((elem as HTMLInputElement)?.value, validationMap);
          nextErrors = {
            ...nextErrors,
            ...messages,
          };
          valid.push(isValid);
        }
      });

    // Find attribute input values.
    const attributesElem = document.getElementById(EVENT_BUILDER_ATTRIBUTES_ID) as HTMLTableElement;
    if (attributesElem) {
      const inputs = Array.from(attributesElem.querySelectorAll(`input`)) as Array<HTMLInputElement>;
      inputs.forEach((input) => {
        const prefix = input.id.split(`-`).shift() as string;
        // @ts-ignore.
        const validationMap: Array<AnyValidator> = fieldValidations.attribute(prefix, input.id as string);
        const { messages, isValid } = doFieldValidation(input.value, validationMap);
        nextErrors = {
          ...nextErrors,
          ...messages,
        };
        valid.push(isValid);
      });
    }

    // Completely replace state instead of appending it.
    updateErrors(nextErrors);
    return every(valid);
  }, [doFieldValidation, fieldValidations, updateErrors]);

  /**
   * Handles the save button click.
   */
  const saveClickHandler = useCallback(() => {
    // Success toast base props.
    const successToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: isCreate ? labels.toastEventSuccessAddedTitle : labels.toastEventSuccessUpdatedTitle,
      toastType: TOAST_TYPE_SUCCESS,
    };

    // Error toast props
    const errorToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      toastContent: <strong>{errorLabels.toastErrorTitle}</strong>,
      toastType: TOAST_TYPE_DANGER,
    };

    // Validate all the values in one go.
    const isValid = validateAllFields();
    if (isValid) {
      // Construct the event to save.
      const eventToSave: EntitySegmentEventSubscription = {
        ...event,
      };
      // Relevance type is always days.
      if (!isUndefined(property(`relevance.value`)(event))) {
        set(eventToSave, `relevance.type`, `days`);
      }
      setIsSaving(true);
      if (eventId) {
        lastValueFrom(segmentationService.updateEvent({ config: eventToSave, id: eventId }))
          .then(() => {
            const toastContent = undefined;
            const toastTitle = labels.toastEventSuccessUpdatedTitle;
            const toastProps = { ...successToastProps, title: toastTitle, toastContent };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastSuccessCreate} />);
            navigate({ pathname: getCapabilityPath(`/events`) });
            return true;
          })
          .finally((): void => {
            setIsSaving(false);
          })
          .catch(() => {
            const toastProps = {
              ...errorToastProps,
              toastContent: (
                <span>
                  {errorLabels.toastEventFailAddedMessage} <em>{eventToSave.name}</em>{' '}
                  {errorLabels.toastEventFailUpdatedMessageEnd}
                </span>
              ),
            };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastFailCreate} />);
            return true;
          });
      } else {
        lastValueFrom(segmentationService.createEvent({ config: eventToSave }))
          .then(() => {
            const toastContent = (
              <span>
                {labels.toastEventSuccessAddedMessage} <em>{eventToSave.name}</em>{' '}
                {labels.toastEventSuccessAddedMessageEnd}
              </span>
            );
            const toastTitle = labels.toastEventSuccessUpdatedTitle;
            const toastProps = { ...successToastProps, title: toastTitle, toastContent };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastSuccessCreate} />);
            navigate({ pathname: getCapabilityPath(`/events`) });
            return true;
          })
          .finally((): void => {
            setIsSaving(false);
          })
          .catch(() => {
            const toastProps = {
              ...errorToastProps,
              toastContent: <span>{errorLabels.toastErrorLabel}</span>,
            };
            updateActiveToast(<Toast {...toastProps} data-id={EVENT_BUILDER_DATA_IDS.toastFailCreate} />);
            return true;
          });
      }
    }
  }, [
    errorLabels.toastErrorLabel,
    errorLabels.toastErrorTitle,
    errorLabels.toastEventFailAddedMessage,
    errorLabels.toastEventFailUpdatedMessageEnd,
    event,
    eventId,
    isCreate,
    labels.toastEventSuccessAddedMessage,
    labels.toastEventSuccessAddedMessageEnd,
    labels.toastEventSuccessAddedTitle,
    labels.toastEventSuccessUpdatedTitle,
    navigate,
    segmentationService,
    updateActiveToast,
    validateAllFields,
  ]);

  const eventTypeSelectProps: SelectFieldProps = useMemo((): SelectFieldProps => {
    return {
      callback: (option: SelectFieldOption): void => {
        let origin = EVENT_SUBSCRIPTION_ORIGIN_CLOUDFLARE;
        if (option.value === EVENT_RULE_TYPE_CUSTOM) {
          origin = EVENT_SUBSCRIPTION_ORIGIN_CLIENT_LAYER;
        }
        if (option.value !== event.type) {
          const nextEvent = {
            ...event,
            origin,
            property: [],
            type: option.value as EntitySegmentEventSubscription['type'],
          };
          setEvent(nextEvent);
        }
      },
      defaultOption: eventOptions.filter((option) => option.value === event.type).shift(),
      errorMessage: errors.source,
      fieldLabel: labels.eventType,
      id: `event-type`,
      name: `event-type`,
      options: eventOptions,
      placeholder: labels.selectEventTypePlaceholder,
      required: true,
      value: event.type,
    };
  }, [errors.source, event, labels.eventType, labels.selectEventTypePlaceholder, setEvent]);

  if (!eventsList) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const sourceSelectProps: SelectFieldProps = {
    callback: (option: SelectFieldOption): void => {
      // Do nothing.
    },
    defaultOption: {
      key: `website-behavior`,
      label: labels.websiteBehavior,
      value: `website-behavior`,
    },
    errorMessage: errors.source,
    fieldLabel: labels.sourceLabel,
    id: `event-source`,
    name: `source`,
    note: labels.eventSourceNote,
    options: [
      {
        key: `website-behavior`,
        label: labels.websiteBehavior,
        value: `website-behavior`,
      },
    ],
    readOnly: true,
    required: true,
    value: ``,
  };

  const EventDetails = (
    <div className="event-builder-web-behavior-form__details">
      <div className="sds-row">
        <TextField
          // data-id={EVENT_BUILDER_DATA_IDS.eventDetailsName}
          errorMessage={errors?.name}
          fieldLabel={labels.eventNameLabel}
          id={EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.name}
          name={EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.name}
          onBlur={(e): void => {
            // @ts-ignore.
            const value = e.target.value;
            const attributeid = generateAttributeId(value);
            // Only trigger a change event on the attribute id input when creating a new feed.
            if (isAttributeIdEditable) {
              const input = document.querySelector(`#${EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.id}`) as HTMLInputElement;
              if (input) {
                input.value = attributeid;
              }
              input.dispatchEvent(new Event(`change`, { bubbles: true }));
            }

            if (isAttributeIdEditable) {
              // ID is only update on create.
              updateEventValue([
                {
                  key: EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.id],
                  value: attributeid,
                },
                {
                  key: EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.name],
                  value,
                },
              ]);
            } else {
              updateEventValue([
                {
                  key: EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.name],
                  value,
                },
              ]);
            }
          }}
          onChange={(e): void => {
            // @ts-ignore.
            const value = e.target.value;
            updateEventValue([
              {
                key: EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.name],
                value,
              },
            ]);
          }}
          note={labels.eventNameNote}
          placeholder={labels.eventNamePlaceholder}
          required
          value={event.name}
        />
        <TextField
          // data-id={EVENT_BUILDER_DATA_IDS.eventDetailsName}
          disabled={!isAttributeIdEditable}
          errorMessage={errors?.id}
          fieldLabel={labels.eventBuilderAttributeIdLabel}
          icon={isAttributeIdEditable ? ICON_LOCK_OPEN : ICON_LOCK}
          id={EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.id}
          name={EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.id}
          note={labels.eventBuilderAttributeIdNote}
          onChange={(e): void => {
            updateEventValue([
              {
                key: EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.id],
                // @ts-ignore.
                value: e.target.value,
              },
            ]);
          }}
          placeholder={labels.eventBuilderAttributeIdPlaceholder}
          required
          defaultValue={event.attributeid}
          value={event.attributeid}
        />
        <SelectField {...sourceSelectProps} ref={null} />
        <SelectField {...eventTypeSelectProps} ref={null} />
        {event.type === `custom` && (
          <TextField
            classes={`event-builder-web-behavior-form__details-custom-id`}
            data-id={EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.uniqueCustomId}
            disabled={!isCreate}
            errorMessage={errors?.custom}
            fieldLabel={labels.eventCustomId}
            id={EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.uniqueCustomId}
            icon={isCreate ? ICON_LOCK_OPEN : ICON_LOCK}
            name={EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.uniqueCustomId}
            note={labels.eventCustomIdNote}
            onChange={(e): void => {
              updateEventValue([
                {
                  key: EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.uniqueCustomId],
                  // @ts-ignore.
                  value: e.target.value,
                },
              ]);
            }}
            placeholder={labels.eventCustomIdPlaceholder}
            required
            value={event.uniqueCustomId || ``}
          />
        )}
        <TextField
          data-id={`event-time-period`}
          errorMessage={errors?.time}
          fieldLabel={labels.eventTimePeriodLabel}
          id={`event-time-period`}
          name={`event-time-period`}
          note={labels.eventTimePeriodNote}
          onChange={(e): void => {
            updateEventValue([
              {
                key: EVENT_BUILDER_FORM_ID_PROPERTY_MAP[EVENT_BUILDER_WEB_BEHAVIOR_FORM_IDS.time],
                // @ts-ignore.
                value: e.target.value,
              },
            ]);
          }}
          placeholder={labels.eventTimePeriodPlaceholder}
          value={(event.relevance?.value as string) || ``}
        />
      </div>
    </div>
  );

  const actionButtons = [];

  if (!isSaving) {
    actionButtons.push(
      <Button
        format={BUTTON_FORMAT_SOFT}
        isLoading={isSaving}
        key="discard-changes"
        onClick={resetChanges}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />,
    );
  }

  actionButtons.push(
    <Button
      data-id={EVENT_BUILDER_DATA_IDS.buttonSaveChanges}
      disabled={some(Object.values(errors))}
      isLoading={isSaving}
      key="save-changes"
      label={isCreate ? labels.addEvent : labels.saveChanges}
      onClick={saveClickHandler}
      text={isCreate ? labels.addEvent : labels.saveChanges}
    />,
  );

  const additionalAttributeProps: Partial<EventBuilderWebBehaviorAttributesProps> = {};
  if (event.type === EVENT_RULE_TYPE_REQUEST || event.type === EVENT_RULE_TYPE_LOGIN) {
    additionalAttributeProps.attributeSelectOptions = property(event.type)(
      attributeNameSelectOptionMap,
    ) as Array<SelectFieldOption>;
  }

  return (
    <>
      {/* Note: this heading needs to exist outside of wrapping elements to get styled correctly */}
      <h2>{labels.eventDetails}</h2>
      <form className="event-builder-web-behavior-form">
        {EventDetails}
        {event.type !== EVENT_RULE_TYPE_VISIT && (
          <EventBuilderWebBehaviorAttributes
            type={event.type}
            errors={errors}
            id={EVENT_BUILDER_ATTRIBUTES_ID}
            operator={event.operator || EVENT_RULE_LOGIC_OPERATOR_AND}
            setOperator={(operator) => {
              setEvent({
                ...event,
                operator,
              });
            }}
            properties={event.property}
            setProperties={(properties, messages): void => {
              const hasAttributeError = some(Object.values(messages));
              if (!hasAttributeError) {
                // Only update the properties if there are no errors found.
                setEvent({
                  ...event,
                  property: properties,
                });
              }

              updateErrors({
                ...errors,
                ...messages,
              });
            }}
            {...additionalAttributeProps}
          />
        )}
        <div className="event-builder-web-behavior-form__actions">
          <ButtonList buttonKeys={[`discard-changes`, `save-changes`]} buttons={actionButtons} />
        </div>
      </form>
    </>
  );
};
